/* global Word */
/**
 * Faxina de ESPAÇAMENTO — remove os parágrafos vazios que sobram no documento e
 * repõe o vazio que a norma exige depois de TABELA quando ele falta.
 *
 * Por que existe: o espaçamento do padrão Norven vem do ESTILO
 * (`spaceBefore`/`spaceAfter` em `houseStyle.ts`), nunca de linha em branco, e a
 * norma diz isso literalmente em `validation_checklist_before_final_answer`
 * ("Não há parágrafo vazio depois de tópico, subtópico, corpo ou item de
 * lista"). Mesmo assim os vazios se acumulam quando vários turnos editam a
 * mesma região: `deleteText` apagava o texto e deixava a marca de parágrafo, e
 * a inserção ANCORADA criava parágrafo novo sem olhar se já havia um vazio ali.
 * As duas causas foram tratadas na origem (ver `tools.ts`), mas prevenção não
 * cobre documento trazido pelo usuário nem edição feita à mão no Word — daí a
 * rede de segurança.
 *
 * QUEM CHAMA: o CLIENTE, ao fim de todo turno que escreveu (`runAgentLoop`),
 * junto do validador. Não é ferramenta exposta ao modelo de propósito: ele
 * esqueceria de chamar (a mesma falha que o problema já demonstra) e gastaria um
 * passo do `LIMIT_STEPS` para uma tarefa que não exige julgamento nenhum.
 *
 * O QUE NUNCA É APAGADO — os três vazios legítimos:
 *  1. o(s) exigido(s) DEPOIS DE TABELA (`table_contract.empty_paragraphs_after_table`,
 *     lido do rules.json — o número é por tipo de documento, não constante);
 *  2. o vazio FINAL do corpo, que o template reserva;
 *  3. qualquer vazio ESTRUTURAL (`isStructuralParagraph`) — a quebra de página
 *     que separa a capa do corpo, ou uma imagem ANCORADA num parágrafo vazio.
 *     Apagar a quebra puxaria o corpo para cima da capa; apagar o parágrafo de
 *     uma âncora levaria a imagem junto.
 * Some-se a isso o PRÓLOGO inteiro (a capa, e as sobrecapas de documentos do
 * template antigo), pulado por `coverPrologueEnd`, e o interior de tabelas.
 */
import { readRulesJson } from './template';
import { coverPrologueEnd, isStructuralParagraph } from './tools';
import { applyHouseParagraphStyle } from './houseStyle';
import { missingBlankAfterTables } from './sectionPlan';
import { dlog } from '../util/log';
/** Teto de parágrafos inspecionados. O mesmo do validador, pelo mesmo motivo. */
const MAX_SCAN_PARAGRAPHS = 800;
/**
 * Índices dos parágrafos vazios REMOVÍVEIS — a decisão da faxina, sem Office.js.
 *
 * Separada do efeito de propósito: é aqui que moram as exceções que fazem a
 * diferença entre limpar e estragar o documento, e elas precisam ser
 * exercitáveis fora do Word (ver `addin/scripts/check-cleanup.mjs`).
 *
 * Fica de fora:
 *  - tudo antes de `prologueEnd` (a capa, inclusive sua quebra de página);
 *  - parágrafo dentro de tabela;
 *  - o ÚLTIMO parágrafo do corpo, reservado pelo template;
 *  - os primeiros `keepAfterTable` vazios que seguem uma tabela (a norma os exige).
 *
 * O que ela NÃO sabe: se o parágrafo é estrutural (imagem ancorada / quebra de
 * página). Isso só o OOXML diz, e o chamador confere depois — é a última trava
 * antes de apagar.
 */
export function removableEmptyIndexes(paragraphs, prologueEnd, keepAfterTable) {
    const vazio = (i) => !paragraphs[i].text.trim();
    const emTabela = (i) => paragraphs[i].inTable;
    const candidatos = [];
    for (let i = Math.max(0, prologueEnd); i < paragraphs.length - 1; i++) {
        if (vazio(i) && !emTabela(i))
            candidatos.push(i);
    }
    if (!candidatos.length)
        return [];
    // Um vazio logo depois de tabela é EXIGIDO pela norma: preserva os primeiros
    // `keepAfterTable` de cada sequência que sucede uma tabela.
    const preservados = new Set();
    for (const i of candidatos) {
        if (i - 1 < 0 || !emTabela(i - 1))
            continue;
        for (let k = 0; k < keepAfterTable; k++) {
            const alvo = i + k;
            if (alvo < paragraphs.length && vazio(alvo) && !emTabela(alvo))
                preservados.add(alvo);
        }
    }
    return candidatos.filter((i) => !preservados.has(i));
}
/** Quantos vazios a norma exige depois de uma tabela. Default 1 (padrão Norven). */
async function emptyParagraphsAfterTable() {
    try {
        const base = await readRulesJson('_base');
        const contrato = base === null || base === void 0 ? void 0 : base.table_contract;
        const n = contrato === null || contrato === void 0 ? void 0 : contrato.empty_paragraphs_after_table;
        return typeof n === 'number' && n >= 0 ? n : 1;
    }
    catch (_a) {
        return 1; // rules.json indisponível: o padrão Norven é 1
    }
}
/**
 * Remove os parágrafos vazios supérfluos do corpo.
 *
 * Best-effort e silencioso: qualquer falha devolve `removed: 0` e loga — uma
 * faxina cosmética jamais pode derrubar o turno ou o chat.
 *
 * Roda DENTRO do turno que escreveu, então entra no mesmo escopo do "Desfazer"
 * (o snapshot é tirado antes da 1ª escrita do turno). Rodar depois disso
 * deixaria a limpeza fora do que o botão reverte.
 */
export async function normalizeSpacing() {
    const vazio = { removed: 0, inserted: 0, truncated: false };
    try {
        const manterAposTabela = await emptyParagraphsAfterTable();
        return await Word.run(async (context) => {
            const paras = context.document.body.paragraphs;
            paras.load('items/text,items/tableNestingLevel');
            await context.sync();
            const todos = paras.items;
            const truncated = todos.length > MAX_SCAN_PARAGRAPHS;
            const itens = todos.slice(0, MAX_SCAN_PARAGRAPHS);
            if (!itens.length)
                return vazio;
            const prologo = await coverPrologueEnd(context, todos);
            const formas = itens.map((p) => {
                var _a;
                return ({
                    text: p.text || '',
                    inTable: ((_a = p.tableNestingLevel) !== null && _a !== void 0 ? _a : 0) > 0,
                });
            });
            const aVerificar = removableEmptyIndexes(formas, prologo, manterAposTabela);
            // Só agora o OOXML, e apenas dos candidatos que sobraram: é a leitura
            // cara, e é a ÚNICA forma confiável de ver que um parágrafo "vazio"
            // sustenta uma imagem ancorada ou carrega quebra de página.
            let removidos = 0;
            if (aVerificar.length) {
                const ooxml = aVerificar.map((i) => itens[i].getOoxml());
                await context.sync();
                const remover = aVerificar.filter((_, k) => !isStructuralParagraph(ooxml[k].value || ''));
                for (const i of remover)
                    itens[i].delete();
                if (remover.length)
                    await context.sync();
                removidos = remover.length;
            }
            // Depois de TABELA, o contrário: onde falta o vazio que a norma exige,
            // ele entra (no estilo de corpo — senão herdaria o estilo do parágrafo
            // seguinte, que pode ser um título). A decisão usa as formas de ANTES da
            // remoção: a remoção nunca toca nos vazios depois de tabela.
            const faltando = missingBlankAfterTables(formas.map((f) => ({ inTable: f.inTable, empty: !f.text.trim() })), manterAposTabela, prologo);
            for (const i of faltando) {
                const branco = itens[i].insertParagraph('', Word.InsertLocation.before);
                applyHouseParagraphStyle(branco, 'body_paragraph');
            }
            if (faltando.length)
                await context.sync();
            if (removidos || faltando.length) {
                dlog('[normalizeSpacing] vazios removidos/inseridos:', removidos, faltando.length);
            }
            return { removed: removidos, inserted: faltando.length, truncated };
        });
    }
    catch (e) {
        console.warn('[normalizeSpacing] falhou (faxina é best-effort):', e);
        return vazio;
    }
}
